import React, { useState } from 'react';
import { Copy, Check, Mail, Clock, Shield, FileSignature } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import {
  Form,
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from './Form';
import { useCreateInvitation } from '../helpers/useInvitationsApi';
import { PermissionLevelArrayValues } from '../helpers/schema';
import styles from './InviteGuestDialog.module.css';

const inviteSchema = z.object({
  email: z.string().email().optional().or(z.literal('')),
  permissionLevel: z.enum(PermissionLevelArrayValues),
  expiresInHours: z.string(), // We use string for select, convert to number on submit
});

interface InviteGuestDialogProps {
  sessionId: string;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const InviteGuestDialog = ({
  sessionId,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: InviteGuestDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [requireICA, setRequireICA] = useState(false);
  const { mutate: createInvitation, isPending } = useCreateInvitation();

  const form = useForm({
    schema: inviteSchema,
    defaultValues: {
      email: '',
      permissionLevel: 'edit',
      expiresInHours: '24',
    },
  });

  const onSubmit = (values: z.infer<typeof inviteSchema>) => {
    createInvitation(
      {
        sessionId,
        permissionLevel: values.permissionLevel,
        expiresInHours: parseInt(values.expiresInHours, 10),
        email: values.email || undefined,
        requireICA,
      },
      {
        onSuccess: (data) => {
          // Construct full URL with current domain
          const baseUrl =
            typeof window !== 'undefined'
              ? `${window.location.protocol}//${window.location.host}`
              : '';
          const fullUrl = data.inviteUrl.startsWith('http')
            ? data.inviteUrl
            : `${baseUrl}${data.inviteUrl}`;
          setInviteUrl(fullUrl);
          toast.success('Invitation created successfully');
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  const copyToClipboard = () => {
    if (inviteUrl) {
      navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard');
    }
  };

  const resetDialog = () => {
    setInviteUrl(null);
    form.setValues({
      email: '',
      permissionLevel: 'view',
      expiresInHours: '24',
    });
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetDialog();
        else setOpen(true);
      }}
    >
      {/* Only render trigger in uncontrolled mode (when open prop is not provided) */}
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          {children || (
            <Button size="icon-sm" variant="ghost">
              Invite
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className={styles.dialogContent}>
        <DialogHeader>
          <DialogTitle>Invite Guest</DialogTitle>
          <DialogDescription>Generate a secure link to share this session.</DialogDescription>
        </DialogHeader>

        {!inviteUrl ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
              <FormItem name="email">
                <FormLabel>Restrict to Email (Optional)</FormLabel>
                <div className={styles.inputWithIcon}>
                  <Mail className={styles.inputIcon} size={16} />
                  <FormControl>
                    <Input
                      placeholder="colleague@example.com"
                      value={form.values.email}
                      onChange={(e) =>
                        form.setValues((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className={styles.input}
                    />
                  </FormControl>
                </div>
                <FormDescription>
                  If provided, only this email can accept the invite.
                </FormDescription>
                <FormMessage />
              </FormItem>

              <div className={styles.row}>
                <FormItem name="permissionLevel" className={styles.halfWidth}>
                  <FormLabel>Permission</FormLabel>
                  <Select
                    value={form.values.permissionLevel}
                    onValueChange={(val: any) =>
                      form.setValues((prev) => ({
                        ...prev,
                        permissionLevel: val,
                      }))
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <div className={styles.selectTriggerContent}>
                          <Shield size={14} />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="view">View Only</SelectItem>
                      <SelectItem value="edit">Can Edit</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>

                <FormItem name="expiresInHours" className={styles.halfWidth}>
                  <FormLabel>Expires In</FormLabel>
                  <Select
                    value={form.values.expiresInHours}
                    onValueChange={(val) =>
                      form.setValues((prev) => ({
                        ...prev,
                        expiresInHours: val,
                      }))
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <div className={styles.selectTriggerContent}>
                          <Clock size={14} />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1">1 Hour</SelectItem>
                      <SelectItem value="24">24 Hours</SelectItem>
                      <SelectItem value="168">7 Days</SelectItem>
                      <SelectItem value="720">30 Days</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              </div>

              {/* Independent Contractor Agreement checkbox */}
              <div className={styles.row} style={{ marginTop: '0.25rem' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    lineHeight: 1.4,
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: requireICA ? '1px solid var(--primary)' : '1px solid var(--border)',
                    background: requireICA ? 'hsl(var(--primary) / 0.05)' : 'transparent',
                    transition: 'all 0.15s ease',
                    width: '100%',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={requireICA}
                    onChange={(e) => setRequireICA(e.target.checked)}
                    style={{ accentColor: 'var(--primary)', marginTop: '2px', flexShrink: 0 }}
                  />
                  <div>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600 }}>
                      <FileSignature size={14} />
                      Require Independent Contractor Agreement
                    </span>
                    <span style={{ color: 'var(--muted-foreground)', fontSize: '0.75rem' }}>
                      Both parties must e-sign an ICA before the participant can access the session. Signatures are stored for future sessions.
                    </span>
                  </div>
                </label>
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? 'Generating...' : 'Generate Link'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          <div className={styles.successState}>
            <div className={styles.successIconWrapper}>
              <Check className={styles.successIcon} size={32} />
            </div>
            <h3 className={styles.successTitle}>Invitation Created</h3>
            <p className={styles.successText}>
              Share this link with your guest. It will expire based on your selection.
            </p>

            <div className={styles.copyContainer}>
              <Input value={inviteUrl} readOnly className={styles.copyInput} />
              <Button
                size="icon"
                variant="outline"
                onClick={copyToClipboard}
                className={styles.copyButton}
              >
                <Copy size={16} />
              </Button>
            </div>

            <DialogFooter>
              <Button onClick={resetDialog} className={styles.fullWidth}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
